import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Check, LoaderCircle, RotateCcw } from "lucide-react";
import { calculatorAddons, spirits } from "@/siteData";
import { cocktailMenu } from "@/craftCocktails";
import { SectionLabel } from "./SectionLabel";
import { MenuCardButton, composeMenuCard } from "./MenuCardButton";

const ALLERGEN_OPTIONS = ["Tree nuts", "Peanuts", "Dairy", "Gluten", "Shellfish", "No pork", "Vegan", "Vegetarian"];
const PRESENTATION_OPTIONS = ["Mobile charcuterie cart", "Grazing table", "Cart + grazing table"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  fullName: "",
  email: "",
  phone: "",
  eventDate: "",
  location: "",
  guestCount: "",
  eventType: "Private celebration",
  serviceStyle: "Signature cocktails + wine & beer",
  durationHours: "3",
  notes: "",
  charcuteriePresentation: "Mobile charcuterie cart",
  dietaryNotes: "",
};

export default function BookingForm({ brief, selectedCocktailIds }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [allergens, setAllergens] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!brief) return;
    setForm((current) => ({
      ...current,
      guestCount: String(brief.guestCount || current.guestCount || ""),
      serviceStyle: brief.serviceStyle || current.serviceStyle,
      durationHours: String(brief.durationHours || current.durationHours),
    }));
  }, [brief]);

  const selectedCocktails = useMemo(() => cocktailMenu.filter((cocktail) => selectedCocktailIds.includes(cocktail.id)), [selectedCocktailIds]);
  const selectedSpiritLabels = useMemo(() => (brief?.selectedSpirits || []).map((id) => spirits.find((spirit) => spirit.id === id)?.label || id), [brief]);
  const selectedAddonLabels = useMemo(() => (brief?.selectedAddons || []).map((id) => calculatorAddons.find((addon) => addon.id === id)?.shortLabel || id), [brief]);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const charcuterieActive = form.serviceStyle === "Bar + charcuterie experience" || form.eventType === "Charcuterie grazing event" || (brief?.selectedAddons || []).includes("charcuterie");
  const toggleAllergen = (item) => setAllergens((current) => (current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item]));

  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setError("");
    let menuCardJpeg = "";
    if (selectedCocktails.length) {
      try {
        const canvas = await composeMenuCard(selectedCocktails);
        menuCardJpeg = canvas.toDataURL("image/jpeg", 0.82).split(",")[1];
      } catch (cardError) {
        console.warn("Menu card render failed, submitting without it:", cardError);
        menuCardJpeg = "";
      }
    }
    try {
      const response = await fetch(`${API}/booking-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          event_date: form.eventDate,
          event_location: form.location,
          guest_count: Number(form.guestCount),
          event_type: form.eventType,
          service_style: form.serviceStyle,
          duration_hours: Number(form.durationHours),
          selected_cocktails: selectedCocktails.map((cocktail) => cocktail.name),
          selected_spirits: selectedSpiritLabels,
          spirit_weights: Object.fromEntries((brief?.selectedSpirits || []).filter((id) => (brief?.spiritWeights?.[id] || 1) > 1).map((id) => [spirits.find((spirit) => spirit.id === id)?.label || id, brief.spiritWeights[id]])),
          selected_addons: brief?.selectedAddons || [],
          notes: form.notes,
          charcuterie_presentation: charcuterieActive ? form.charcuteriePresentation : "",
          dietary_allergens: charcuterieActive ? allergens : [],
          dietary_notes: charcuterieActive ? form.dietaryNotes : "",
          menu_card_jpeg: menuCardJpeg,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || "We could not send your request.");
      setStatus("success");
    } catch (submitError) {
      setStatus("error");
      setError(submitError.message || "We could not send your request. Please try again.");
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setAllergens([]);
    setStatus("idle");
    setError("");
  };

  return (
    <section className="section booking-section" id="reserve" data-testid="booking-inquiry-section">
      <div className="section-intro reveal-on-scroll">
        <SectionLabel number="07" testId="booking-section-label">The private reservation</SectionLabel>
        <div className="intro-grid">
          <h2 data-testid="booking-heading">Let’s make the room remember you.</h2>
          <p data-testid="booking-description">Tell us the shape of your affair. We’ll return with a considered quote, a service plan, and the few details that turn a good night into your signature night.</p>
        </div>
      </div>

      <div className="booking-layout">
        <div className="booking-side reveal-on-scroll">
          <div className="booking-side-number">07</div>
          <p className="booking-side-quote">“The best hosts are the ones who get to be guests.”</p>
          <div className="booking-side-rule" />
          <p className="booking-side-note">Serving private celebrations, weddings, executive gatherings, and intimate soirées throughout Miami-Dade, Broward, and Palm Beach.</p>
          <div className="booking-side-contact"><span>Prefer a direct note?</span><a href="mailto:carlos@popsocialevents.com" data-testid="booking-direct-email">carlos@popsocialevents.com</a></div>
        </div>

        <div className="booking-form-wrap reveal-on-scroll">
          {status === "success" ? (
            <div className="booking-success" data-testid="booking-form-success-banner">
              <span className="success-mark"><Check size={19} /></span>
              <div><span className="card-eyebrow">Request received</span><h3>Your evening is now on our radar.</h3><p>We’ve sent your brief to the Pop Social team. Expect a thoughtful reply with next steps and a tailored quote.</p></div>
              <button type="button" className="text-button" data-testid="booking-reset-button" onClick={reset}><RotateCcw size={15} /> Start another brief</button>
            </div>
          ) : (
            <form className="booking-form" onSubmit={submit} data-testid="booking-inquiry-form">
              <div className="form-heading"><span className="card-eyebrow">Tell us the essentials</span><span className="form-step">01 — 03</span></div>
              <div className="form-grid">
                <label className="form-field"><span>Name / host</span><input className="input-control" required value={form.fullName} onChange={(event) => update("fullName", event.target.value)} data-testid="booking-input-name" placeholder="Your full name" /></label>
                <label className="form-field"><span>Email address</span><input className="input-control" type="email" required value={form.email} onChange={(event) => update("email", event.target.value)} data-testid="booking-input-email" placeholder="you@example.com" /></label>
                <label className="form-field"><span>Phone number</span><input className="input-control" type="tel" required value={form.phone} onChange={(event) => update("phone", event.target.value)} data-testid="booking-input-phone" placeholder="(305) 000-0000" /></label>
                <label className="form-field"><span>Event date</span><input className="input-control" type="date" required value={form.eventDate} onChange={(event) => update("eventDate", event.target.value)} data-testid="booking-input-date" /></label>
                <label className="form-field field-wide"><span>City / venue</span><input className="input-control" required value={form.location} onChange={(event) => update("location", event.target.value)} data-testid="booking-input-location" placeholder="Miami Beach, private residence, venue name…" /></label>
                <label className="form-field"><span>Guest count</span><input className="input-control" type="number" min="1" max="1000" required value={form.guestCount} onChange={(event) => update("guestCount", event.target.value)} data-testid="booking-input-guests" placeholder="75" /></label>
                <label className="form-field"><span>Event type</span><select className="input-control" value={form.eventType} onChange={(event) => update("eventType", event.target.value)} data-testid="booking-input-event-type"><option>Private celebration</option><option>Luxury wedding</option><option>Corporate soirée</option><option>Charcuterie grazing event</option><option>Other</option></select></label>
                <label className="form-field"><span>Active service hours</span><select className="input-control" value={form.durationHours} onChange={(event) => update("durationHours", event.target.value)} data-testid="booking-input-hours"><option value="2">2 hours</option><option value="3">3 hours</option><option value="4">4 hours</option><option value="5">5 hours</option><option value="6">6 hours</option><option value="7">7 hours</option></select></label>
                <label className="form-field field-wide"><span>Service direction</span><select className="input-control" value={form.serviceStyle} onChange={(event) => update("serviceStyle", event.target.value)} data-testid="booking-input-service-style"><option>Signature cocktails + wine & beer</option><option>Full custom mixology bar</option><option>Beer & wine service</option><option>Bar + charcuterie experience</option></select></label>
                {charcuterieActive && (
                  <div className="dietary-panel field-wide" data-testid="dietary-panel">
                    <span className="card-eyebrow">Charcuterie & dietary care</span>
                    <div className="dietary-group">
                      <span className="dietary-label">How should we present the grazing experience?</span>
                      <div className="dietary-options">
                        {PRESENTATION_OPTIONS.map((option) => (
                          <button type="button" key={option} className={`dietary-chip${form.charcuteriePresentation === option ? " is-active" : ""}`} onClick={() => update("charcuteriePresentation", option)} aria-pressed={form.charcuteriePresentation === option} data-testid={`presentation-chip-${option.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{option}</button>
                        ))}
                      </div>
                    </div>
                    <div className="dietary-group">
                      <span className="dietary-label">Allergens & preferences we should honour</span>
                      <div className="dietary-options">
                        {ALLERGEN_OPTIONS.map((option) => (
                          <button type="button" key={option} className={`dietary-chip${allergens.includes(option) ? " is-active" : ""}`} onClick={() => toggleAllergen(option)} aria-pressed={allergens.includes(option)} data-testid={`allergen-chip-${option.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{option}</button>
                        ))}
                      </div>
                    </div>
                    <label className="form-field"><span>Dietary notes for the boards</span><textarea className="input-control textarea-control" rows="2" value={form.dietaryNotes} onChange={(event) => update("dietaryNotes", event.target.value)} data-testid="dietary-notes-input" placeholder="Severe allergies, kosher/halal needs, kids' boards, favourite cheeses…" /></label>
                  </div>
                )}
                <label className="form-field field-wide"><span>Anything we should know?</span><textarea className="input-control textarea-control" rows="4" value={form.notes} onChange={(event) => update("notes", event.target.value)} data-testid="booking-input-notes" placeholder="Mood, menu ideas, dress code, access notes, dietary details…" /></label>
              </div>

              <div className="brief-summary" data-testid="booking-selection-summary">
                <div><span className="card-eyebrow">Your current brief</span><strong>{brief?.result?.estimatedTotal ? `$${brief.result.estimatedTotal.toLocaleString()} planning estimate` : "No estimate yet"}</strong><MenuCardButton cocktails={selectedCocktails} /></div>
                <div className="brief-chips">
                  {selectedCocktails.length ? selectedCocktails.map((cocktail) => <span className="brief-chip" key={cocktail.id}>{cocktail.name}</span>) : <span className="brief-chip is-muted">No cocktails selected yet</span>}
                  {selectedSpiritLabels.map((spirit) => <span className="brief-chip" key={spirit}>{spirit}</span>)}
                  {selectedAddonLabels.map((addon) => <span className="brief-chip" key={addon}>{addon}</span>)}
                </div>
              </div>

              {status === "error" && <div className="booking-error" role="alert" data-testid="booking-form-error">{error}</div>}
              <div className="form-footer"><span>We reply personally · South Florida</span><button type="submit" className="button button-primary" disabled={status === "sending"} data-testid="booking-submit-button">{status === "sending" ? <><LoaderCircle className="spin" size={16} /> Sending…</> : <>Request my quote <ArrowUpRight size={16} /></>}</button></div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
